import type { ExperimentConfig, ExperimentStep, ExperimentPart } from '@/types';

// ============================================================================
// EXPERIMENT 5: The Copper Cycle
// A visually stunning experiment with dramatic color changes
// ============================================================================

// Complete Color Transition Guide for Copper Cycle
export const copperCycleColors = {
  // Copper states
  copperMetalTarnished: '#8B4513',   // Dark copper (tarnished)
  copperMetalClean: '#B87333',       // Bright copper (clean)

  // Reaction 1: Cu + HNO3
  no2Gas: '#8B4513',                 // Brown NO2 gas
  cuNO32Solution: '#40E0D0',         // Turquoise/Blue-Green - Cu(NO3)2
  reaction1Mid: '#3CB371',           // Greenish during dissolution

  // Reaction 2: Cu(NO3)2 + NaOH
  cuOH2Precipitate: '#1E90FF',       // Dodger Blue - Cu(OH)2
  reaction2Mid: '#5F9EA0',           // During precipitation

  // Reaction 3: Cu(OH)2 -> CuO (heating)
  cuOH2Heating1: '#4169E1',          // Royal blue (warming)
  cuOH2Heating2: '#483D8B',          // Dark slate blue
  cuOH2Heating3: '#2F2F4F',          // Very dark blue
  cuOSolid: '#1A1A1A',               // Black CuO

  // Reaction 4: CuO + H2SO4
  cuO_H2SO4_Mid1: '#4B0082',         // Indigo (black with blue)
  cuO_H2SO4_Mid2: '#0000CD',         // Medium blue
  cuSO4Solution: '#0066CC',          // Bright blue CuSO4

  // Reaction 5: CuSO4 + Zn
  cuSO4ZnPhase1: '#1E90FF',          // Dodger blue
  cuSO4ZnPhase2: '#48D1CC',          // Medium turquoise
  cuSO4ZnPhase3: '#ADD8E6',          // Light blue
  cuSO4ZnPhase4: '#E0FFFF',          // Light cyan
  znSO4Supernatant: '#FFFFFF',       // Colorless (ZnSO4)
  copperPrecipitate: '#B87333',      // Copper metal recovered

  // Other visual elements
  zincPowder: '#A0A0A0',
  steelWool: '#C0C0C0',
  naohSolution: '#FFFFFF',
  h2so4Solution: '#FFFFFF',
  hno3Solution: '#FFFACD',
  distilledWater: '#E6F3FF',
  methanol: '#FFFFFF',
  acetone: '#FFFFFF',
  boilingChips: '#D3D3D3',
  porcelainDish: '#FFFAFA',

  // Gas colors with opacity
  gases: {
    no2: { color: '#8B4513', opacity: 0.6 },
    h2: { color: '#FFFFFF', opacity: 0.3 },
    steam: { color: '#F8F8FF', opacity: 0.4 },
  },
};

// Reaction data for educational content
export interface ReactionData {
  id: number;
  name: string;
  type: 'redox' | 'double_displacement' | 'dehydration';
  equation: string;
  startColor: string;
  endColor: string;
  description: string;
}

export const reactions: ReactionData[] = [
  {
    id: 1,
    name: 'Copper + Nitric Acid',
    type: 'redox',
    equation: 'Cu + 4HNO3 -> Cu(NO3)2 + 2NO2 + 2H2O',
    startColor: copperCycleColors.copperMetalClean,
    endColor: copperCycleColors.cuNO32Solution,
    description: 'Copper is oxidized (loses electrons) while nitrogen in HNO3 is reduced. Brown NO2 gas is evolved.',
  },
  {
    id: 2,
    name: 'Copper Nitrate + Sodium Hydroxide',
    type: 'double_displacement',
    equation: 'Cu(NO3)2 + 2NaOH -> Cu(OH)2 + 2NaNO3',
    startColor: copperCycleColors.cuNO32Solution,
    endColor: copperCycleColors.cuOH2Precipitate,
    description: 'A double displacement reaction produces blue copper(II) hydroxide precipitate.',
  },
  {
    id: 3,
    name: 'Copper Hydroxide Dehydration',
    type: 'dehydration',
    equation: 'Cu(OH)2 -> CuO + H2O',
    startColor: copperCycleColors.cuOH2Precipitate,
    endColor: copperCycleColors.cuOSolid,
    description: 'Heating removes water from Cu(OH)2, converting it to black copper(II) oxide.',
  },
  {
    id: 4,
    name: 'Copper Oxide + Sulfuric Acid',
    type: 'double_displacement',
    equation: 'CuO + H2SO4 -> CuSO4 + H2O',
    startColor: copperCycleColors.cuOSolid,
    endColor: copperCycleColors.cuSO4Solution,
    description: 'The black CuO dissolves in acid to form bright blue copper sulfate solution.',
  },
  {
    id: 5,
    name: 'Copper Sulfate + Zinc',
    type: 'redox',
    equation: 'CuSO4 + Zn -> ZnSO4 + Cu',
    startColor: copperCycleColors.cuSO4Solution,
    endColor: copperCycleColors.copperPrecipitate,
    description: 'Zinc displaces copper from solution. As zinc dissolves, copper metal precipitates.',
  },
];

// Part 1: Introduction and Setup
const part1Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Safety Check',
    shortDescription: 'Review safety procedures for the copper cycle',
    handler: 'handleSafetyAcknowledgement',
    infoContent: {
      title: 'Lab Safety - IMPORTANT',
      content: `This experiment uses concentrated acids and generates toxic gases. Follow these precautions:
- Work ONLY in the fume hood when using HNO3
- Wear safety goggles and gloves at all times
- Concentrated HNO3 and H2SO4 are very corrosive
- NO2 gas is toxic - keep fume hood sash down
- Methanol and acetone are flammable - keep away from flames
- Hydrogen gas is flammable during Zn displacement`,
      whatHappens: 'Critical safety protocols are reviewed for this multi-step experiment involving concentrated acids, toxic gases, and flammable solvents.',
      observe: [
        'Verify the fume hood is functional and sash can be lowered',
        'Confirm safety goggles and gloves are available',
        'Note which steps require the fume hood (HNO3 and Zn reactions)',
      ],
    },
  },
  {
    id: 2,
    title: 'Gather Materials',
    shortDescription: 'Collect all required equipment and chemicals',
    handler: 'handleGatherMaterials',
    validation: {
      requires: ['safetyAcknowledged'],
    },
  },
];

// Part 2: Prepare Copper Wire
const part2Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Clean Copper Wire',
    shortDescription: 'Clean about 30 cm copper wire using steel wool or emery cloth',
    handler: 'handleCleanCopper',
    infoContent: {
      title: 'Why Clean the Copper?',
      content: `Clean about **30 cm** of copper wire using **steel wool or emery cloth**. Copper tarnishes when exposed to air, forming a layer of copper oxide. Cleaning removes this oxide layer and any other surface contaminants. This ensures we start with pure copper and can accurately measure our initial mass.`,
      whatHappens: 'Steel wool or emery cloth abrades away the dark copper oxide tarnish layer, revealing the bright reddish-orange pure copper surface beneath.',
      observe: [
        'The tarnished wire appears dull brown before cleaning',
        'After cleaning, the copper wire becomes bright and shiny reddish-orange',
        'The removed tarnish is the oxide layer (CuO/Cu2O) that forms in air',
      ],
    },
  },
  {
    id: 2,
    title: 'Cut and Coil Wire',
    shortDescription: 'Cut the copper wire into pieces',
    handler: 'handleCutAndCoil',
    validation: {
      requires: ['copperCleaned'],
    },
  },
  {
    id: 3,
    title: 'Weigh Copper',
    shortDescription: 'Weigh approximately 0.50 g of copper wire',
    handler: 'handleWeighCopper',
    validation: {
      requires: ['copperCoiled'],
    },
  },
  {
    id: 4,
    title: 'Record Initial Mass',
    shortDescription: 'Record the exact mass of copper',
    handler: 'handleRecordInitialMass',
    validation: {
      requires: ['copperWeighed'],
    },
  },
];

// Part 3: Reaction 1 - Cu + HNO3
const part3Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Add Copper to Beaker',
    shortDescription: 'Place copper wire in 400 mL beaker (in fume hood)',
    handler: 'handleAddCopperToBeaker',
    infoContent: {
      title: 'Preparing for Reaction 1',
      content: `This reaction MUST be performed in the fume hood. Concentrated nitric acid will react vigorously with copper, producing toxic brown NO2 gas.`,
      whatHappens: 'The copper wire is placed in a beaker inside the fume hood in preparation for the vigorous, gas-producing reaction with nitric acid.',
      observe: [
        'Ensure the beaker is placed squarely inside the fume hood',
        'The copper wire should be bright and clean from the previous step',
        'Lower the fume hood sash before proceeding with acid addition',
      ],
    },
  },
  {
    id: 2,
    title: 'Add Concentrated HNO3',
    shortDescription: 'Carefully add 4-5 mL concentrated HNO3',
    handler: 'handleAddHNO3',
    validation: {
      requires: ['copperInBeaker'],
    },
    infoContent: {
      title: 'Redox Reaction',
      content: `A redox (reduction-oxidation) reaction involves the transfer of electrons between species. In this reaction:
- Copper (Cu) is OXIDIZED: Cu -> Cu2+ + 2e-
- Nitrogen (in HNO3) is REDUCED: N+5 -> N+4 (in NO2)

The brown gas you see is nitrogen dioxide (NO2), a toxic gas.`,
      chemicalEquation: 'Cu + 4HNO3 -> Cu(NO3)2 + 2NO2 + 2H2O',
      whatHappens: 'Copper metal dissolves in concentrated nitric acid through a redox reaction, producing a blue-green Cu(NO3)2 solution and toxic brown NO2 gas.',
      observe: [
        'Brown NO2 gas evolves vigorously from the beaker',
        'The solution gradually turns blue-green as Cu2+ ions form',
        'The copper wire dissolves progressively as the reaction proceeds',
        'The reaction is exothermic and the solution warms noticeably',
      ],
    },
  },
  {
    id: 3,
    title: 'Observe Reaction',
    shortDescription: 'Observe colour change, gas evolution, and temperature change',
    handler: 'handleObserveReaction1',
    validation: {
      requires: ['hno3Added'],
    },
  },
  {
    id: 4,
    title: 'Add Distilled Water',
    shortDescription: 'Add 100 mL distilled water after reaction subsides',
    handler: 'handleAddWater1',
    validation: {
      requires: ['reaction1Observed'],
    },
  },
  {
    id: 5,
    title: 'Record Observation',
    shortDescription: 'Record the blue-green color of Cu(NO3)2 solution',
    handler: 'handleRecordReaction1',
    validation: {
      requires: ['water1Added'],
    },
  },
];

// Part 4: Reaction 2 - Cu(NO3)2 + NaOH
const part4Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Prepare NaOH',
    shortDescription: 'Measure 30 mL of 3 M NaOH',
    handler: 'handlePrepareNaOH',
    infoContent: {
      title: 'Double Displacement Reaction',
      content: `In a double displacement (metathesis) reaction, the cations and anions of two compounds switch partners:

Cu(NO3)2 + 2NaOH -> Cu(OH)2 + 2NaNO3

The blue precipitate is copper(II) hydroxide, which is insoluble in water.`,
      chemicalEquation: 'Cu(NO3)2 + 2NaOH -> Cu(OH)2(s) + 2NaNO3',
      whatHappens: 'NaOH is measured for addition to the Cu(NO3)2 solution; the hydroxide ions will combine with Cu2+ to form an insoluble blue precipitate.',
      observe: [
        'Measure 30 mL of 3 M NaOH carefully using a graduated cylinder',
        'The NaOH solution is colorless and should be handled with care',
        'Prepare for a dramatic color change when added to the blue-green solution',
      ],
    },
  },
  {
    id: 2,
    title: 'Add NaOH Slowly',
    shortDescription: 'Slowly add NaOH to the Cu(NO3)2 solution',
    handler: 'handleAddNaOH',
    validation: {
      requires: ['naohPrepared'],
    },
  },
  {
    id: 3,
    title: 'Observe Precipitate',
    shortDescription: 'Observe the bright blue Cu(OH)2 precipitate forming',
    handler: 'handleObserveReaction2',
    validation: {
      requires: ['naohAdded'],
    },
  },
  {
    id: 4,
    title: 'Record Observation',
    shortDescription: 'Record the blue gelatinous precipitate',
    handler: 'handleRecordReaction2',
    validation: {
      requires: ['reaction2Observed'],
    },
  },
];

// Part 5: Reaction 3 - Cu(OH)2 -> CuO
const part5Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Add Boiling Chips',
    shortDescription: 'Weigh 2-3 boiling chips, record exact mass, then add to beaker',
    handler: 'handleAddBoilingChips',
    infoContent: {
      title: 'Boiling Chips & Dehydration',
      content: `**Weigh 2-3 boiling chips** and record the exact weight — this mass must be subtracted from the final product mass.

When heated, copper(II) hydroxide loses water to form copper(II) oxide:

Cu(OH)2 -> CuO + H2O

Notice how the bright blue precipitate turns black. This is because CuO (black) has different electronic properties than Cu(OH)2 (blue).`,
      chemicalEquation: 'Cu(OH)2 -> CuO + H2O',
      whatHappens: 'Weighed boiling chips are added to prevent bumping during heating; the subsequent heating drives off water from Cu(OH)2, converting it to black CuO.',
      observe: [
        'Record the exact mass of 2-3 boiling chips before adding them',
        'During heating, the bright blue Cu(OH)2 precipitate gradually turns black',
        'The color transition passes through dark blue and nearly black stages',
      ],
    },
  },
  {
    id: 2,
    title: 'Heat Solution',
    shortDescription: 'Heat carefully to boiling with stirring',
    handler: 'handleHeatToBoil',
    validation: {
      requires: ['boilingChipsAdded'],
    },
  },
  {
    id: 3,
    title: 'Observe Color Change',
    shortDescription: 'Watch the blue-to-black color transition',
    handler: 'handleObserveReaction3',
    validation: {
      requires: ['solutionHeated'],
    },
  },
  {
    id: 4,
    title: 'Record Observation',
    shortDescription: 'Record the formation of black CuO',
    handler: 'handleRecordReaction3',
    validation: {
      requires: ['reaction3Observed'],
    },
  },
];

// Part 6: Decant and Wash
const part6Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Allow CuO to Settle',
    shortDescription: 'Wait for black CuO to settle to the bottom',
    handler: 'handleAllowSettling',
  },
  {
    id: 2,
    title: 'Decant Supernatant',
    shortDescription: 'Carefully pour off the clear supernatant',
    handler: 'handleDecant1',
    validation: {
      requires: ['cuoSettled'],
    },
    infoContent: {
      title: 'Decanting',
      content: `Decanting is the process of carefully pouring off liquid from a settled solid. This removes the NaNO₃ (sodium nitrate) supernatant from the CuO solid. (Note: If you do not know how to decant, ask for help.)`,
      whatHappens: 'The clear supernatant liquid containing dissolved NaNO3 is carefully poured off, leaving the black CuO solid in the beaker.',
      observe: [
        'The black CuO settles to the bottom of the beaker',
        'The supernatant liquid should be relatively clear',
        'Pour slowly and steadily to avoid disturbing the settled solid',
      ],
    },
  },
  {
    id: 3,
    title: 'Add Wash Water',
    shortDescription: 'Add 200 mL hot distilled water',
    handler: 'handleAddWashWater',
    validation: {
      requires: ['supernatantDecanted'],
    },
  },
  {
    id: 4,
    title: 'Stir and Settle',
    shortDescription: 'Stir to wash CuO, then allow to settle',
    handler: 'handleStirAndSettle',
    validation: {
      requires: ['washWaterAdded'],
    },
  },
  {
    id: 5,
    title: 'Decant Again',
    shortDescription: 'Decant the wash water',
    handler: 'handleDecant2',
    validation: {
      requires: ['washStirred'],
    },
  },
];

// Part 7: Reaction 4 - CuO + H2SO4
const part7Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Prepare H2SO4',
    shortDescription: 'Measure 15 mL of 6 M H2SO4',
    handler: 'handlePrepareH2SO4',
    infoContent: {
      title: 'Dissolving CuO',
      content: `The black CuO dissolves in sulfuric acid to form bright blue copper sulfate solution. This is another double displacement reaction.`,
      chemicalEquation: 'CuO + H2SO4 -> CuSO4 + H2O',
      whatHappens: 'Sulfuric acid is measured for addition to the black CuO solid; the acid will dissolve the oxide to form a bright blue CuSO4 solution.',
      observe: [
        'Measure 15 mL of 6 M H2SO4 carefully',
        'The black CuO solid is ready in the beaker from the previous step',
        'This step produces a striking transformation from black to bright blue',
      ],
    },
  },
  {
    id: 2,
    title: 'Add H2SO4 Slowly',
    shortDescription: 'Slowly add 15 mL of 6 M H2SO4 to the CuO',
    handler: 'handleAddH2SO4',
    validation: {
      requires: ['h2so4Prepared'],
    },
  },
  {
    id: 3,
    title: 'Observe Dissolution',
    shortDescription: 'Watch the black solid dissolve into blue solution',
    handler: 'handleObserveReaction4',
    validation: {
      requires: ['h2so4Added'],
    },
  },
  {
    id: 4,
    title: 'Record Observation',
    shortDescription: 'Record the bright blue CuSO4 solution',
    handler: 'handleRecordReaction4',
    validation: {
      requires: ['reaction4Observed'],
    },
  },
];

// Part 8: Reaction 5 - CuSO4 + Zn
const part8Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Move to Fume Hood',
    shortDescription: 'Ensure you are working in the fume hood',
    handler: 'handleMoveFumeHood',
    infoContent: {
      title: 'Metal Displacement Reaction',
      content: `Zinc is more reactive than copper (higher in the activity series), so it can displace copper from its compounds:

CuSO4 + Zn -> ZnSO4 + Cu

Zinc loses electrons: Zn -> Zn2+ + 2e- (oxidation)
Copper gains electrons: Cu2+ + 2e- -> Cu (reduction)

The hydrogen gas bubbles come from zinc reacting with excess acid.`,
      chemicalEquation: 'CuSO4 + Zn -> ZnSO4 + Cu',
      whatHappens: 'This final redox reaction must be performed in the fume hood because zinc reacting with excess acid produces flammable hydrogen gas.',
      observe: [
        'Ensure you are working in the fume hood with the sash lowered',
        'The bright blue CuSO4 solution is ready for the zinc displacement reaction',
        'Hydrogen gas (flammable) will be evolved during this reaction',
      ],
    },
  },
  {
    id: 2,
    title: 'Add Zinc Powder',
    shortDescription: 'Add 2.0 g of zinc powder in small amounts, stirring until supernatant is clear',
    handler: 'handleAddZinc',
    validation: {
      requires: ['inFumeHood'],
    },
  },
  {
    id: 3,
    title: 'Stir Continuously',
    shortDescription: 'Stir while observing color changes',
    handler: 'handleStirZincReaction',
    validation: {
      requires: ['zincAdded'],
    },
  },
  {
    id: 4,
    title: 'Observe Color Fading',
    shortDescription: 'Watch the blue color fade as copper precipitates',
    handler: 'handleObserveColorFading',
    validation: {
      requires: ['stirring'],
    },
  },
  {
    id: 5,
    title: 'Heat Gently',
    shortDescription: 'When gas evolution becomes very slow, heat gently (do not boil) and allow to cool',
    handler: 'handleHeatGently',
    validation: {
      requires: ['colorFading'],
    },
  },
  {
    id: 6,
    title: 'Wait for Clear Supernatant',
    shortDescription: 'Continue until supernatant is colorless',
    handler: 'handleWaitForClear',
    validation: {
      requires: ['heated'],
    },
  },
  {
    id: 7,
    title: 'Record Observation',
    shortDescription: 'Record the copper metal precipitate',
    handler: 'handleRecordReaction5',
    validation: {
      requires: ['supernatantClear'],
    },
  },
];

// Part 9: Wash and Dry
const part9Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Pre-Weigh Evaporating Dish',
    shortDescription: 'Weigh and record the mass of the empty porcelain evaporating dish before use',
    handler: 'handleWeighDish',
    infoContent: {
      title: 'Pre-Weighing the Dish',
      content: 'The porcelain evaporating dish must be **pre-weighed** and its mass recorded. This mass will be subtracted from the final (dish + copper) mass to determine the recovered copper mass alone.',
      whatHappens: 'The empty porcelain evaporating dish is weighed on an analytical balance so its mass can later be subtracted from the total to find the recovered copper mass.',
      observe: [
        'Record the dish mass to the precision of the balance',
        'Ensure the dish is clean and dry before weighing',
        'This mass is critical for calculating the final copper yield',
      ],
    },
  },
  {
    id: 2,
    title: 'Transfer Copper',
    shortDescription: 'When gas evolution has ceased, decant the solution and quantitatively transfer precipitate to pre-weighed dish',
    handler: 'handleTransferCopper',
    validation: {
      requires: ['dishWeighed'],
    },
  },
  {
    id: 3,
    title: 'Wash with Water',
    shortDescription: 'Wash copper with about 5 mL deionized water, allow to settle, decant. Repeat.',
    handler: 'handleWashWithWater',
    validation: {
      requires: ['copperTransferred'],
    },
  },
  {
    id: 4,
    title: 'Wash with Methanol',
    shortDescription: 'Wash with about 5 mL methanol (FLAMMABLE - do not use near an open flame), settle and decant',
    handler: 'handleWashWithMethanol',
    validation: {
      requires: ['waterWashComplete'],
    },
  },
  {
    id: 5,
    title: 'Wash with Acetone',
    shortDescription: 'Wash with about 5 mL acetone (VERY FLAMMABLE - BE CAREFUL), settle and decant',
    handler: 'handleWashWithAcetone',
    validation: {
      requires: ['methanolWashComplete'],
    },
    infoContent: {
      title: 'Why Use Acetone?',
      content: `Acetone evaporates very quickly, helping to dry the copper faster. It also removes any remaining organic residue. The volatile nature of acetone means most will evaporate within minutes.`,
      whatHappens: 'Acetone displaces the methanol and water from the copper surface and evaporates rapidly, accelerating the drying process.',
      observe: [
        'Acetone is very volatile and evaporates quickly from the copper surface',
        'Keep away from all open flames as acetone is extremely flammable',
        'The copper should appear drier after the acetone wash and decanting',
      ],
    },
  },
  {
    id: 6,
    title: 'Air Dry',
    shortDescription: 'Air dry the product for at least 5 minutes',
    handler: 'handleAirDry',
    validation: {
      requires: ['acetoneWashComplete'],
    },
  },
  {
    id: 7,
    title: 'Oven Dry',
    shortDescription: 'Further dry in drying oven at 80 °C for 15 minutes',
    handler: 'handleOvenDry',
    validation: {
      requires: ['airDried'],
    },
  },
];

// Part 10: Final Weighing and Calculations
const part10Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Weigh Final Copper',
    shortDescription: 'Transfer dried copper to previously weighed weighing boat, weigh copper plus boiling chips',
    handler: 'handleWeighFinalCopper',
  },
  {
    id: 2,
    title: 'Calculate Copper Mass',
    shortDescription: 'Calculate the weight of copper by subtracting weighing boat and boiling chips mass',
    handler: 'handleCalculateCopperMass',
    validation: {
      requires: ['finalWeighed'],
    },
  },
  {
    id: 3,
    title: 'Calculate Percent Yield',
    shortDescription: 'Compare with initial weight and calculate percent yield',
    handler: 'handleCalculateYield',
    validation: {
      requires: ['copperMassCalculated'],
    },
    infoContent: {
      title: 'Understanding Percent Yield',
      content: `The percent yield of the copper can be expressed as a ratio of that obtained to that expected, multiplied by 100:

**% yield = (actual yield / theoretical yield) x 100**

**Hint:** In this experiment, the "theoretical yield" of Cu is the original weight of copper wire. A yield over 100% suggests contamination or incomplete drying. A yield under 100% indicates loss during transfers or washing.`,
      whatHappens: 'The recovered copper mass is compared to the initial copper mass to calculate percent yield, which evaluates the quality of laboratory technique.',
      observe: [
        'A yield near 100% indicates excellent technique with minimal losses',
        'Yields over 100% suggest contamination (zinc, moisture) in the product',
        'Yields under 100% indicate copper was lost during transfers or washing',
      ],
    },
  },
  {
    id: 4,
    title: 'Submit Results',
    shortDescription: 'Submit your completed experiment data',
    handler: 'handleSubmitResults',
    validation: {
      requires: ['yieldCalculated'],
    },
  },
];

// Initial States
const part1InitialState = {
  safetyAcknowledged: false,
  materialsGathered: false,
};

const part2InitialState = {
  copperCleaned: false,
  copperCoiled: false,
  copperWeighed: false,
  initialMass: 0,
  copperColor: copperCycleColors.copperMetalTarnished,
};

const part3InitialState = {
  copperInBeaker: false,
  hno3Added: false,
  reaction1Observed: false,
  water1Added: false,
  solutionColor: copperCycleColors.copperMetalClean,
  gasEvolution: false,
};

const part4InitialState = {
  naohPrepared: false,
  naohAdded: false,
  reaction2Observed: false,
  solutionColor: copperCycleColors.cuNO32Solution,
  precipitateFormed: false,
};

const part5InitialState = {
  boilingChipsAdded: false,
  boilingChipsMass: 0,
  solutionHeated: false,
  reaction3Observed: false,
  solutionColor: copperCycleColors.cuOH2Precipitate,
};

const part6InitialState = {
  cuoSettled: false,
  supernatantDecanted: false,
  washWaterAdded: false,
  washStirred: false,
  secondDecantComplete: false,
};

const part7InitialState = {
  h2so4Prepared: false,
  h2so4Added: false,
  reaction4Observed: false,
  solutionColor: copperCycleColors.cuOSolid,
};

const part8InitialState = {
  inFumeHood: false,
  zincAdded: false,
  stirring: false,
  colorFading: false,
  heated: false,
  supernatantClear: false,
  solutionColor: copperCycleColors.cuSO4Solution,
  h2Bubbles: false,
};

const part9InitialState = {
  dishWeighed: false,
  dishMass: 0,
  copperTransferred: false,
  waterWashComplete: false,
  methanolWashComplete: false,
  acetoneWashComplete: false,
  airDried: false,
  ovenDried: false,
};

const part10InitialState = {
  finalWeighed: false,
  finalMassWithDish: 0,
  copperMassCalculated: false,
  finalCopperMass: 0,
  yieldCalculated: false,
  percentYield: 0,
};

// Part Definitions
const part1: ExperimentPart = {
  id: 1,
  name: 'introduction',
  title: 'Part I: Introduction & Setup',
  steps: part1Steps,
  equipment: ['Fume hood', 'Safety goggles', 'Gloves'],
  initialState: part1InitialState,
};

const part2: ExperimentPart = {
  id: 2,
  name: 'prepareCopper',
  title: 'Part II: Prepare Copper Wire',
  steps: part2Steps,
  equipment: ['Copper wire (~30 cm)', 'Steel wool or emery cloth', 'Scissors', 'Analytical balance', 'Weigh boat'],
  initialState: part2InitialState,
};

const part3: ExperimentPart = {
  id: 3,
  name: 'reaction1',
  title: 'Part III: Reaction 1 - Cu + HNO3 (REDOX)',
  steps: part3Steps,
  equipment: ['400 mL beaker', 'Concentrated HNO3', 'Distilled water', 'Fume hood'],
  initialState: part3InitialState,
};

const part4: ExperimentPart = {
  id: 4,
  name: 'reaction2',
  title: 'Part IV: Reaction 2 - Cu(NO3)2 + NaOH',
  steps: part4Steps,
  equipment: ['3 M NaOH', 'Graduated cylinder'],
  initialState: part4InitialState,
};

const part5: ExperimentPart = {
  id: 5,
  name: 'reaction3',
  title: 'Part V: Reaction 3 - Cu(OH)2 Dehydration',
  steps: part5Steps,
  equipment: ['Boiling chips', 'Hot plate', 'Stirring rod', 'Balance'],
  initialState: part5InitialState,
};

const part6: ExperimentPart = {
  id: 6,
  name: 'wash1',
  title: 'Part VI: Decant and Wash',
  steps: part6Steps,
  equipment: ['Hot distilled water'],
  initialState: part6InitialState,
};

const part7: ExperimentPart = {
  id: 7,
  name: 'reaction4',
  title: 'Part VII: Reaction 4 - CuO + H2SO4',
  steps: part7Steps,
  equipment: ['6 M H2SO4', 'Graduated cylinder'],
  initialState: part7InitialState,
};

const part8: ExperimentPart = {
  id: 8,
  name: 'reaction5',
  title: 'Part VIII: Reaction 5 - CuSO4 + Zn (REDOX)',
  steps: part8Steps,
  equipment: ['Zinc powder', 'Stirring rod', 'Hot plate', 'Fume hood'],
  initialState: part8InitialState,
};

const part9: ExperimentPart = {
  id: 9,
  name: 'wash2',
  title: 'Part IX: Wash and Dry Copper',
  steps: part9Steps,
  equipment: ['Porcelain evaporating dish', 'Deionized water', 'Methanol', 'Acetone', 'Oven'],
  initialState: part9InitialState,
};

const part10: ExperimentPart = {
  id: 10,
  name: 'calculations',
  title: 'Part X: Final Weighing & Calculations',
  steps: part10Steps,
  equipment: ['Analytical balance', 'Calculator'],
  initialState: part10InitialState,
};

// ============================================================================
// FULL EXPERIMENT CONFIGURATION
// ============================================================================

const experiment5Config: ExperimentConfig = {
  id: 'exp5',
  number: 5,
  name: 'The Copper Cycle',
  description: 'A visually stunning demonstration of five chemical reactions that transform copper through different forms and back to metal, illustrating redox, precipitation, and decomposition reactions with dramatic color changes.',
  estimatedTime: '150 minutes',
  learningObjectives: [
    'Observe and understand redox (oxidation-reduction) reactions',
    'Understand double displacement (metathesis) reactions',
    'Observe dehydration reactions with heating',
    'Understand the activity series of metals',
    'Calculate percent yield and evaluate lab technique',
    'Observe characteristic color changes of copper compounds',
    'Practice quantitative transfer and washing techniques',
  ],
  safetyNotes: [
    'Good housekeeping is essential - clean up any spilled materials in or near the balance immediately',
    'Handle sample material with a clean spatula only - never return anything to the bottle',
    'Work ONLY in the fume hood when using concentrated acids',
    'Wear safety goggles and gloves at all times',
    'Concentrated HNO₃ produces toxic NO₂ gas',
    'H₂SO₄ is very corrosive - handle with extreme care',
    'Methanol is FLAMMABLE - do not use near an open flame',
    'Acetone is VERY FLAMMABLE - be careful',
    'Hydrogen gas is flammable during zinc reaction',
    'Wash hands thoroughly after the experiment',
    'Disposal: Nitric acid, sodium hydroxide, sulphuric acid, methanol, acetone',
  ],
  requiredEquipment: [
    'Fume hood',
    'Copper wire (~0.50 g, about 30 cm length)',
    'Steel wool or emery cloth',
    '400 mL beaker',
    'Analytical balance',
    'Weigh boat',
    'Concentrated HNO₃',
    '3 M NaOH (30 mL)',
    '6 M H₂SO₄ (15 mL)',
    'Zinc powder (2.0 g)',
    'Boiling chips (2-3, weighed)',
    'Hot plate',
    'Stirring rod',
    'Porcelain evaporating dish',
    'Distilled water',
    'Deionized water',
    'Methanol (FLAMMABLE)',
    'Acetone (VERY FLAMMABLE)',
    'Drying oven (80 °C)',
    'Calculator',
  ],
  parts: [part1, part2, part3, part4, part5, part6, part7, part8, part9, part10],
  quiz: {
    experimentId: 'exp5',
    title: 'Copper Cycle Quiz',
    questions: [
      {
        id: 'exp5-q1',
        type: 'mcq' as const,
        question: 'In Reaction 1 of the copper cycle, copper metal is dissolved in concentrated HNO₃. What is the brown gas evolved?',
        options: [
          { id: 'a', text: 'Cl₂ (chlorine)' },
          { id: 'b', text: 'SO₂ (sulfur dioxide)' },
          { id: 'c', text: 'NO₂ (nitrogen dioxide)' },
          { id: 'd', text: 'H₂ (hydrogen)' },
        ],
        correctOptionId: 'c',
        explanation: 'Concentrated nitric acid is an oxidizing acid. It oxidizes copper metal while being reduced to NO₂, a toxic brown gas: Cu + 4HNO₃(conc) → Cu(NO₃)₂ + 2NO₂\u2191 + 2H₂O.',
      },
      {
        id: 'exp5-q2',
        type: 'mcq' as const,
        question: 'What color change is observed when NaOH is added to Cu(NO₃)₂ solution?',
        options: [
          { id: 'a', text: 'Blue-green solution becomes colorless' },
          { id: 'b', text: 'Blue-green solution produces a blue Cu(OH)₂ precipitate' },
          { id: 'c', text: 'Colorless solution turns yellow' },
          { id: 'd', text: 'Blue-green solution turns red' },
        ],
        correctOptionId: 'b',
        explanation: 'NaOH provides OH⁻ ions that react with Cu\u00B2⁺ to form insoluble copper(II) hydroxide: Cu\u00B2⁺ + 2OH⁻ → Cu(OH)₂\u2193. This is a blue gelatinous precipitate.',
      },
      {
        id: 'exp5-q3',
        type: 'mcq' as const,
        question: 'In the final step, zinc metal is added to CuSO₄ solution to recover copper. This is an example of:',
        options: [
          { id: 'a', text: 'A decomposition reaction' },
          { id: 'b', text: 'An acid-base reaction' },
          { id: 'c', text: 'A single displacement (redox) reaction' },
          { id: 'd', text: 'A double displacement reaction' },
        ],
        correctOptionId: 'c',
        explanation: 'Zinc is more active than copper in the activity series. Zinc is oxidized (Zn → Zn\u00B2⁺ + 2e⁻) while Cu\u00B2⁺ is reduced (Cu\u00B2⁺ + 2e⁻ → Cu). This is a single displacement redox reaction: Zn + CuSO₄ → ZnSO₄ + Cu.',
      },
      {
        id: 'exp5-q4',
        type: 'short_answer' as const,
        question: 'Explain why the percent yield in the copper cycle might be less than 100%. Identify at least two sources of copper loss.',
        sampleAnswer: 'The percent yield is typically less than 100% because copper can be lost at several stages: (1) incomplete transfer between beakers during decanting and washing steps, (2) some copper may remain in solution if not all Cu\u00B2⁺ is reduced by zinc, (3) small particles of copper may be lost during filtration or washing, (4) copper may adhere to glassware walls, and (5) some copper may be lost as spattering during the initial reaction with HNO₃.',
      },
      {
        id: 'exp5-q5',
        type: 'calculation' as const,
        question: 'If you started with 0.500 g of copper wire and recovered 0.423 g of copper at the end of the cycle, what is the percent yield?',
        expectedValue: 84.6,
        tolerance: 2,
        unit: '%',
      },
    ],
  },
};

export default experiment5Config;
